// ============================================
// CHÁMOMILE - Review Service with Firebase
// Customer reviews and ratings system
// Version 2.1 - Fixed Promo Code Usage Logic
// ============================================

import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  limit,
  doc,
  increment
} from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../config/firebase';

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface Review {
  reviewId: string;
  productId: string;
  productName: string;
  customerName: string;
  customerPhone?: string;
  rating: number; // 1-5
  title?: string;
  comment: string;
  orderId?: string; // Optional - links to order for verification
  imageUrl?: string | null; // Customer photo
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Timestamp | string;
  updatedAt?: Timestamp | string;
  isVerifiedPurchase: boolean;
  helpfulCount?: number;
  response?: string; // Owner's response
  responseDate?: Timestamp | string;
}

export interface ProductRating {
  productId: string;
  averageRating: number;
  totalReviews: number;
  ratingDistribution: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
}

export interface ReviewToken {
  token: string;
  orderNumber: string;
  customerPhone: string;
  customerName: string;
  purchasedItems?: Array<{ id: string; name: string; quantity: number }>; // Items from the order
  createdAt: Timestamp | string;
  expiresAt: Timestamp | string;
  isUsed: boolean;
  usedAt?: Timestamp | string;
}

export interface DiscountCode {
  code: string;
  type: 'percentage' | 'fixed';
  value: number;
  description: string;
  minOrder: number;
  maxUses: number;
  usedCount: number;
  active: boolean;
  createdAt: Timestamp | string;
  expiresAt: Timestamp | string;
  reviewId?: string;
}

// ============================================
// REVIEW REWARD KILL SWITCH
// ============================================
// The rewards programme is being redesigned (Aug 2026). While `false`, reviews
// are asked for on their own merit: no discount is promised in the follow-up
// message, no discount code is generated on submission, and nothing is shown
// to the customer afterwards.
//
// Existing THANKS* codes already issued remain valid and redeemable — this only
// stops new ones being created. Flip back to `true` to restore the programme.
export const REVIEW_REWARD_ENABLED = false;

// ============================================
// HELPER FUNCTIONS
// ============================================

// Generate review ID
export const generateReviewId = (): string => {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `REV-${timestamp}-${random}`;
};

// Generate secure token (16 characters)
export const generateReviewToken = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // No confusing chars (0,O,1,I)
  let token = '';
  for (let i = 0; i < 16; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
};

// Generate discount code
export const generateDiscountCode = (): string => {
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `THANKS${random}`;
};

// Format date for display
export const formatReviewDate = (date: Timestamp | string | Date): string => {
  let d: Date;
  if (date instanceof Timestamp) {
    d = date.toDate();
  } else if (typeof date === 'string') {
    d = new Date(date);
  } else {
    d = date;
  }

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else {
    return d.toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
};

// ============================================
// LOCAL STORAGE FALLBACK
// ============================================

const LOCAL_STORAGE_KEY = 'chamomile-reviews';
const LOCAL_TOKENS_KEY = 'chamomile-review-tokens';

const getLocalReviews = (): Review[] => {
  try {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

const saveLocalReviews = (reviews: Review[]): void => {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(reviews));
  } catch (error) {
    console.warn('⚠️ Could not save reviews to localStorage:', error);
  }
};

const getLocalTokens = (): ReviewToken[] => {
  try {
    const data = localStorage.getItem(LOCAL_TOKENS_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

const saveLocalTokens = (tokens: ReviewToken[]): void => {
  try {
    localStorage.setItem(LOCAL_TOKENS_KEY, JSON.stringify(tokens));
  } catch (error) {
    console.warn('⚠️ Could not save tokens to localStorage:', error);
  }
};

// ============================================
// SINGLE-USE TOKEN SYSTEM
// ============================================

/**
 * Create a secure single-use review link
 * Call this when generating follow-up messages
 */
export const createReviewLink = async (order: {
  orderNumber: string;
  customerPhone: string;
  customerName: string;
  items?: Array<{ id: string; name: string; quantity: number }>; // Purchased items for per-flavor reviews
}): Promise<{ token: string; reviewUrl: string }> => {
  const token = generateReviewToken();
  const baseUrl = 'https://chamomilemy.netlify.app';

  // Token expires in 30 days
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 30);

  const tokenData: ReviewToken = {
    token,
    orderNumber: order.orderNumber,
    customerPhone: order.customerPhone || '',
    customerName: order.customerName || '',
    purchasedItems: order.items || [],
    createdAt: new Date().toISOString(),
    expiresAt: expiresAt.toISOString(),
    isUsed: false,
  };

  // Save to Firebase
  if (isFirebaseConfigured() && db) {
    try {
      await setDoc(doc(db, 'review-tokens', token), {
        ...tokenData,
        createdAt: serverTimestamp(),
      });
      console.log('✅ Review token created:', token);
    } catch (error) {
      console.error('❌ Error saving token to Firebase:', error);
    }
  } else {
    // Fallback to localStorage
    const tokens = getLocalTokens();
    tokens.push(tokenData);
    saveLocalTokens(tokens);
  }

  return {
    token,
    reviewUrl: `${baseUrl}/review?token=${token}`,
  };
};

/**
 * Validate a review token
 * Returns order details if valid, or error if invalid/used/expired
 */
export const validateReviewToken = async (token: string): Promise<{
  valid: boolean;
  orderNumber?: string;
  customerName?: string;
  customerPhone?: string;
  purchasedItems?: Array<{ id: string; name: string; quantity: number }>;
  error?: string;
}> => {
  if (!token || token.length !== 16) {
    return { valid: false, error: 'Invalid token format' };
  }

  // Check Firebase
  if (isFirebaseConfigured() && db) {
    try {
      const tokenDoc = await getDoc(doc(db, 'review-tokens', token));

      if (!tokenDoc.exists()) {
        return { valid: false, error: 'Invalid review link. Please use the link sent to you via WhatsApp.' };
      }

      const tokenData = tokenDoc.data() as ReviewToken;

      // Check if already used
      if (tokenData.isUsed) {
        return { valid: false, error: 'This review link has already been used. Each link can only be used once.' };
      }

      // Check expiry
      const expiresAt = tokenData.expiresAt instanceof Timestamp
        ? tokenData.expiresAt.toDate()
        : new Date(tokenData.expiresAt);

      if (new Date() > expiresAt) {
        return { valid: false, error: 'This review link has expired. Please contact us for a new link.' };
      }

      console.log('📦 Token data from Firebase:', { orderNumber: tokenData.orderNumber, purchasedItems: tokenData.purchasedItems });

      return {
        valid: true,
        orderNumber: tokenData.orderNumber,
        customerName: tokenData.customerName,
        customerPhone: tokenData.customerPhone,
        purchasedItems: tokenData.purchasedItems || [],
      };
    } catch (error) {
      console.error('❌ Error validating token:', error);
      return { valid: false, error: 'Error validating review link. Please try again.' };
    }
  }

  // Fallback to localStorage
  const tokens = getLocalTokens();
  const tokenData = tokens.find(t => t.token === token);

  if (!tokenData) {
    return { valid: false, error: 'Invalid review link.' };
  }

  if (tokenData.isUsed) {
    return { valid: false, error: 'This review link has already been used.' };
  }

  const expiresAt = tokenData.expiresAt instanceof Timestamp
    ? tokenData.expiresAt.toDate()
    : new Date(tokenData.expiresAt);
  if (new Date() > expiresAt) {
    return { valid: false, error: 'This review link has expired.' };
  }

  return {
    valid: true,
    orderNumber: tokenData.orderNumber,
    customerName: tokenData.customerName,
    customerPhone: tokenData.customerPhone,
    purchasedItems: tokenData.purchasedItems || [],
  };
};

/**
 * Mark a token as used (call after successful review submission)
 */
export const markTokenAsUsed = async (token: string): Promise<void> => {
  if (isFirebaseConfigured() && db) {
    try {
      await updateDoc(doc(db, 'review-tokens', token), {
        isUsed: true,
        usedAt: serverTimestamp(),
      });
      console.log('✅ Token marked as used:', token);
    } catch (error) {
      console.error('❌ Error marking token as used:', error);
    }
  } else {
    // Fallback to localStorage
    const tokens = getLocalTokens();
    const tokenIndex = tokens.findIndex(t => t.token === token);
    if (tokenIndex >= 0) {
      tokens[tokenIndex].isUsed = true;
      tokens[tokenIndex].usedAt = new Date().toISOString();
      saveLocalTokens(tokens);
    }
  }
};

/**
 * Validate legacy review access (backward compatibility)
 * For old links with ?order=XXX&phone=XXX format
 */
export const validateLegacyReviewAccess = async (
  orderNumber: string,
  customerPhone?: string
): Promise<{
  valid: boolean;
  customerName?: string;
  error?: string;
}> => {
  if (!orderNumber) {
    return { valid: false, error: 'Invalid review link.' };
  }

  if (!isFirebaseConfigured() || !db) {
    // Can't validate without Firebase, allow access but warn
    console.warn('⚠️ Firebase not configured, allowing legacy access');
    return { valid: true };
  }

  try {
    // Check if order exists
    const ordersRef = collection(db, 'online-orders');
    const q = query(ordersRef, where('orderNumber', '==', orderNumber), limit(1));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return { valid: false, error: 'Order not found. Please check your review link.' };
    }

    const orderData = snapshot.docs[0].data();

    // Optionally verify phone number
    if (customerPhone) {
      const orderPhone = orderData.customerInfo?.phone?.replace(/\D/g, '');
      const inputPhone = customerPhone.replace(/\D/g, '');
      if (orderPhone && inputPhone && !orderPhone.includes(inputPhone) && !inputPhone.includes(orderPhone)) {
        return { valid: false, error: 'Phone number does not match order.' };
      }
    }

    // Check if already reviewed this order
    const reviewsRef = collection(db, 'reviews');
    const reviewQuery = query(reviewsRef, where('orderId', '==', orderNumber), limit(1));
    const reviewSnapshot = await getDocs(reviewQuery);

    if (!reviewSnapshot.empty) {
      return { valid: false, error: 'You have already submitted a review for this order.' };
    }

    return {
      valid: true,
      customerName: orderData.customerInfo?.name,
    };
  } catch (error) {
    console.error('❌ Error validating legacy access:', error);
    return { valid: false, error: 'Error validating review link.' };
  }
};

// ============================================
// DISCOUNT CODE MANAGEMENT
// ============================================

/**
 * Create a discount code for a reviewer (4-5 star reviews)
 * Now creates with proper fields for POS compatibility
 */
export const createReviewDiscountCode = async (
  reviewId: string,
  orderId?: string,
  discountValue: number = 10,
  descriptionPrefix: string = 'Review Thank You'
): Promise<string | null> => {
  const code = generateDiscountCode();

  // Expires in 30 days (1 month)
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 30);

  // Format dates for POS compatibility
  const now = new Date();
  const validFrom = now.toISOString().split('T')[0]; // YYYY-MM-DD
  const validTo = expiresAt.toISOString().split('T')[0]; // YYYY-MM-DD

  // Create promo with POS-compatible structure
  const promoData = {
    code,
    // POS compatibility fields
    status: 'active', // POS checks for status === 'active'
    name: orderId ? `Review Reward - ${orderId}` : descriptionPrefix,
    type: 'percentage',
    value: discountValue, // 10% off
    description: orderId
      ? `${discountValue}% off reward for reviewing order ${orderId}`
      : `${descriptionPrefix} - ${discountValue}% Off`,
    minPurchase: 0, // No minimum order
    maxDiscount: 50, // Cap at RM50
    usageLimit: 1, // Single use only
    usageCount: 0,
    // Legacy fields for backward compatibility
    active: true,
    minOrder: 0,
    maxUses: 1,
    usedCount: 0,
    // Date fields
    validFrom,
    validTo,
    createdAt: new Date().toISOString(),
    expiresAt: expiresAt.toISOString(),
    // Metadata
    source: 'review',
    reviewId,
    orderId: orderId || null,
  };

  if (isFirebaseConfigured() && db) {
    try {
      await setDoc(doc(db, 'promos', code), {
        ...promoData,
        createdAt: serverTimestamp(),
        startDate: serverTimestamp(),
      });
      console.log('✅ Discount code created:', code);
      return code;
    } catch (error) {
      console.error('❌ Error creating discount code:', error);
      return null;
    }
  }

  return code; // Return code even without Firebase
};

/**
 * Validate a review discount code (for POS)
 */
export const validateReviewDiscountCode = async (code: string): Promise<{
  valid: boolean;
  discount?: { type: string; value: number; description: string; minOrder: number };
  error?: string;
}> => {
  if (!code || !code.startsWith('THANKS')) {
    return { valid: false, error: 'Invalid code format' };
  }

  if (!isFirebaseConfigured() || !db) {
    return { valid: false, error: 'Cannot validate code offline' };
  }

  try {
    const promoDoc = await getDoc(doc(db, 'promos', code.toUpperCase()));

    if (!promoDoc.exists()) {
      return { valid: false, error: 'Code not found' };
    }

    const promo = promoDoc.data() as DiscountCode;

    if (!promo.active) {
      return { valid: false, error: 'Code is no longer active' };
    }

    if (promo.usedCount >= promo.maxUses) {
      return { valid: false, error: 'Code has already been used' };
    }

    const expiresAt = promo.expiresAt instanceof Timestamp
      ? promo.expiresAt.toDate()
      : new Date(promo.expiresAt);

    if (new Date() > expiresAt) {
      return { valid: false, error: 'Code has expired' };
    }

    return {
      valid: true,
      discount: {
        type: promo.type,
        value: promo.value,
        description: promo.description,
        minOrder: promo.minOrder,
      },
    };
  } catch (error) {
    console.error('❌ Error validating discount code:', error);
    return { valid: false, error: 'Error validating code' };
  }
};

/**
 * Mark a discount code as used - with PROPER BUSINESS LOGIC
 * 
 * BUSINESS RULES:
 * 1. Review-generated codes (source: 'review') - One-time use, become inactive after 1 use
 * 2. Regular promo codes - Follow their usageLimit/maxUses setting
 *    - If usageLimit is set: become inactive when usedCount >= usageLimit
 *    - If no limit (0 or undefined): never auto-deactivate based on usage
 * 3. Always increment usedCount (don't overwrite)
 * 4. Track usageCount for compatibility with some promo structures
 */
export const markDiscountCodeAsUsed = async (code: string): Promise<boolean> => {
  if (!isFirebaseConfigured() || !db) {
    console.warn('⚠️ Firebase not configured, cannot mark code as used');
    return false;
  }

  const normalizedCode = code.toUpperCase().trim();

  try {
    // First, try to get the promo document by ID (code as document ID)
    let promoRef = doc(db, 'promos', normalizedCode);
    let promoSnap = await getDoc(promoRef);

    // If not found by ID, search by 'code' field
    if (!promoSnap.exists()) {
      console.log('🔍 Promo not found by ID, searching by code field...');
      const promosQuery = query(
        collection(db, 'promos'),
        where('code', '==', normalizedCode)
      );
      const querySnapshot = await getDocs(promosQuery);

      if (!querySnapshot.empty) {
        promoSnap = querySnapshot.docs[0];
        promoRef = doc(db, 'promos', promoSnap.id);
      } else {
        console.warn('⚠️ Promo code not found:', normalizedCode);
        return false;
      }
    }

    const promoData = promoSnap.data();
    if (!promoData) {
      console.warn('⚠️ Promo document has no data:', normalizedCode);
      return false;
    }

    // Get current usage count (handle both field names for compatibility)
    const currentUsedCount = promoData.usedCount || promoData.usageCount || 0;
    const newUsedCount = currentUsedCount + 1;

    // Get usage limit (handle different field names)
    // maxUses, usageLimit, or usageLimit field
    const usageLimit = promoData.maxUses || promoData.usageLimit || 0;

    // Determine if this is a review-generated code
    const isReviewCode = promoData.source === 'review';

    // Determine if code should become inactive
    // Rule: Review codes always deactivate after 1 use
    // Rule: Regular codes deactivate when usedCount >= usageLimit (if limit > 0)
    let shouldDeactivate = false;

    if (isReviewCode) {
      // Review codes are always one-time use
      shouldDeactivate = true;
      console.log('📝 Review code - will deactivate after this use');
    } else if (usageLimit > 0 && newUsedCount >= usageLimit) {
      // Regular code has reached its usage limit
      shouldDeactivate = true;
      console.log(`🔢 Usage limit reached: ${newUsedCount}/${usageLimit} - will deactivate`);
    } else if (usageLimit > 0) {
      console.log(`📊 Usage: ${newUsedCount}/${usageLimit} - code remains active`);
    } else {
      console.log(`♾️ Unlimited usage code - usage count: ${newUsedCount}`);
    }

    // Build update object
    const updateData: any = {
      usedCount: newUsedCount,
      usageCount: newUsedCount, // For compatibility
      lastUsedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Only change active status if it should deactivate
    if (shouldDeactivate) {
      updateData.active = false;
      updateData.status = 'inactive';
    }

    // Update the promo document
    await updateDoc(promoRef, updateData);

    console.log(`✅ Promo code "${normalizedCode}" updated:`, {
      newUsedCount,
      usageLimit: usageLimit || 'unlimited',
      deactivated: shouldDeactivate,
      isReviewCode
    });

    return true;

  } catch (error: any) {
    // If document doesn't exist error, try with increment
    if (error.code === 'not-found') {
      console.warn('⚠️ Promo document not found for update:', normalizedCode);
      return false;
    }

    console.error('❌ Error marking discount code as used:', error);

    // Fallback: Try a simpler update with increment
    try {
      const promoRef = doc(db, 'promos', normalizedCode);
      await updateDoc(promoRef, {
        usedCount: increment(1),
        usageCount: increment(1),
        lastUsedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      console.log('✅ Fallback update successful (increment only)');
      return true;
    } catch (fallbackError) {
      console.error('❌ Fallback update also failed:', fallbackError);
      return false;
    }
  }
};

// ============================================
// WHATSAPP MESSAGE GENERATION
// ============================================

/**
 * Generate WhatsApp message for 7-day review follow-up
 * Now creates a secure single-use token
 */
export const generateReviewFollowUpMessage = async (order: {
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  items?: Array<{ id: string; name: string; quantity: number }>; // Purchased items for per-flavor reviews
}): Promise<{ message: string; whatsappUrl: string; reviewUrl: string }> => {
  const firstName = order.customerName?.split(' ')[0] || 'there';

  // Create secure single-use token with items for per-flavor reviews
  const { reviewUrl } = await createReviewLink({
    orderNumber: order.orderNumber,
    customerPhone: order.customerPhone,
    customerName: order.customerName,
    items: order.items || [],
  });

  const message = `Hi ${firstName}! 👋

Thank you for ordering from Chámomile! 🍵

We'd love to hear about your experience! Your feedback helps us improve and helps other customers discover their perfect blend.

📝 *Click to Review:*
${reviewUrl}

A photo of your drink would make it even lovelier 📸

⚠️ *Note:* This link can only be used once.

It only takes 2 minutes and means the world to us! 💛

Thank you for being part of the Chámomile family!

— The Chámomile Team
🏠 Forest Hill Residences, KK`;

  // Generate WhatsApp URL
  const cleanPhone = order.customerPhone?.replace(/\D/g, '') || '';
  const phoneWithCountry = cleanPhone.startsWith('60') ? cleanPhone : `60${cleanPhone.replace(/^0/, '')}`;
  const whatsappUrl = `https://wa.me/${phoneWithCountry}?text=${encodeURIComponent(message)}`;

  return {
    message,
    whatsappUrl,
    reviewUrl,
  };
};

/**
 * Generate bulk follow-up messages
 */
export const generateBulkFollowUpMessages = async (orders: Array<{
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  items?: Array<{ id: string; name: string; quantity: number }>;
}>): Promise<Array<{ order: typeof orders[0]; message: string; whatsappUrl: string; reviewUrl: string }>> => {
  const results = [];
  for (const order of orders) {
    const messageData = await generateReviewFollowUpMessage({
      orderNumber: order.orderNumber,
      customerName: order.customerName,
      customerPhone: order.customerPhone,
      items: order.items || [],
    });
    results.push({ order, ...messageData });
  }
  return results;
};

// ============================================
// REVIEW OPERATIONS
// ============================================

/**
 * Submit a new review (status will be 'pending' until approved by staff)
 * If token is provided, it will be marked as used
 */
export const submitReview = async (reviewData: {
  token?: string; // Single-use token (if using secure link)
  productId: string;
  productName?: string;
  customerName: string;
  customerPhone?: string;
  rating: number;
  title?: string;
  comment: string;
  orderId?: string;
  imageUrl?: string | null;
}): Promise<{ success: boolean; reviewId?: string; discountCode?: string; error?: string }> => {
  const reviewId = generateReviewId();

  // Validate rating
  if (reviewData.rating < 1 || reviewData.rating > 5) {
    return { success: false, error: 'Rating must be between 1 and 5' };
  }

  // Validate comment length
  if (reviewData.comment.length < 10) {
    return { success: false, error: 'Review must be at least 10 characters' };
  }

  if (reviewData.comment.length > 500) {
    return { success: false, error: 'Review must be under 500 characters' };
  }

  // Get product name if not provided
  const productName = reviewData.productName || `Product ${reviewData.productId}`;

  const review: Review = {
    reviewId,
    productId: reviewData.productId,
    productName,
    customerName: reviewData.customerName.trim(),
    customerPhone: reviewData.customerPhone?.trim(),
    rating: reviewData.rating,
    title: reviewData.title?.trim(),
    comment: reviewData.comment.trim(),
    orderId: reviewData.orderId?.trim(),
    imageUrl: reviewData.imageUrl || null,
    status: 'pending',
    createdAt: new Date().toISOString(),
    isVerifiedPurchase: !!reviewData.orderId,
  };

  // Check if Firebase is configured
  if (!isFirebaseConfigured() || !db) {
    console.log('🔧 Firebase not configured - saving review to localStorage');
    try {
      const existingReviews = getLocalReviews();
      existingReviews.push(review);
      saveLocalReviews(existingReviews);
      console.log('✅ Review saved to localStorage:', reviewId);

      // Mark token as used (localStorage)
      if (reviewData.token) {
        await markTokenAsUsed(reviewData.token);
      }

      return { success: true, reviewId };
    } catch (error) {
      console.error('❌ Error saving review to localStorage:', error);
      return { success: false, error: 'Failed to save review' };
    }
  }

  try {
    const reviewWithTimestamp = {
      ...review,
      createdAt: serverTimestamp(),
    };

    await addDoc(collection(db, 'reviews'), reviewWithTimestamp);
    console.log('✅ Review submitted to Firestore:', reviewId);

    // Mark token as used
    if (reviewData.token) {
      await markTokenAsUsed(reviewData.token);
    }

    // Create discount code for 4-5 star reviews
    let discountCode: string | null = null;
    if (REVIEW_REWARD_ENABLED && reviewData.rating >= 4) {
      const hasPhoto = !!reviewData.imageUrl;
      const discountValue = hasPhoto ? 10 : 5;
      const descPrefix = hasPhoto ? 'Review Reward (Photo)' : 'Review Reward';

      discountCode = await createReviewDiscountCode(
        reviewId,
        reviewData.orderId,
        discountValue,
        descPrefix
      );
    }

    return { success: true, reviewId, discountCode: discountCode || undefined };
  } catch (error) {
    console.error('❌ Error submitting review:', error);
    return { success: false, error: 'Failed to submit review. Please try again.' };
  }
};

/**
 * Get all approved reviews for a specific product
 */
export const getProductReviews = async (productId: string): Promise<Review[]> => {
  if (!isFirebaseConfigured() || !db) {
    // Return approved reviews from localStorage
    const reviews = getLocalReviews();
    return reviews
      .filter(r => r.productId === productId && r.status === 'approved')
      .sort((a, b) => new Date(b.createdAt as string).getTime() - new Date(a.createdAt as string).getTime());
  }

  try {
    const reviewsRef = collection(db, 'reviews');
    const q = query(
      reviewsRef,
      where('productId', '==', productId),
      where('status', '==', 'approved'),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as Review);
  } catch (error) {
    console.error('❌ Error fetching product reviews:', error);
    return [];
  }
};

/**
 * Get all approved reviews (for homepage/featured)
 */
export const getAllApprovedReviews = async (): Promise<Review[]> => {
  if (!isFirebaseConfigured() || !db) {
    const reviews = getLocalReviews();
    return reviews
      .filter(r => r.status === 'approved')
      .sort((a, b) => new Date(b.createdAt as string).getTime() - new Date(a.createdAt as string).getTime());
  }

  try {
    const reviewsRef = collection(db, 'reviews');
    const q = query(
      reviewsRef,
      where('status', '==', 'approved'),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as Review);
  } catch (error) {
    console.error('❌ Error fetching all reviews:', error);
    return [];
  }
};

/**
 * Get rating summary for a product
 */
export const getProductRating = async (productId: string): Promise<ProductRating | null> => {
  const reviews = await getProductReviews(productId);

  if (reviews.length === 0) {
    return null;
  }

  const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  let totalRating = 0;

  reviews.forEach(review => {
    const rating = Math.round(review.rating) as 1 | 2 | 3 | 4 | 5;
    if (rating >= 1 && rating <= 5) {
      distribution[rating]++;
      totalRating += review.rating;
    }
  });

  return {
    productId,
    averageRating: totalRating / reviews.length,
    totalReviews: reviews.length,
    ratingDistribution: distribution,
  };
};

/**
 * Get ratings for all products (for displaying on product cards)
 */
export const getAllProductRatings = async (productIds: string[]): Promise<Map<string, ProductRating>> => {
  const ratingsMap = new Map<string, ProductRating>();

  // Fetch all approved reviews
  const allReviews = await getAllApprovedReviews();

  // Group by product
  productIds.forEach(productId => {
    const productReviews = allReviews.filter(r => r.productId === productId);

    if (productReviews.length > 0) {
      const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      let totalRating = 0;

      productReviews.forEach(review => {
        const rating = Math.round(review.rating) as 1 | 2 | 3 | 4 | 5;
        if (rating >= 1 && rating <= 5) {
          distribution[rating]++;
          totalRating += review.rating;
        }
      });

      ratingsMap.set(productId, {
        productId,
        averageRating: totalRating / productReviews.length,
        totalReviews: productReviews.length,
        ratingDistribution: distribution,
      });
    }
  });

  return ratingsMap;
};

/**
 * Get recent reviews (for homepage testimonials)
 */
export const getRecentReviews = async (count: number = 5): Promise<Review[]> => {
  if (!isFirebaseConfigured() || !db) {
    const reviews = getLocalReviews();
    return reviews
      .filter(r => r.status === 'approved')
      .sort((a, b) => new Date(b.createdAt as string).getTime() - new Date(a.createdAt as string).getTime())
      .slice(0, count);
  }

  try {
    const reviewsRef = collection(db, 'reviews');
    const q = query(
      reviewsRef,
      where('status', '==', 'approved'),
      orderBy('createdAt', 'desc'),
      limit(count)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as Review);
  } catch (error) {
    console.error('❌ Error fetching recent reviews:', error);
    return [];
  }
};

/**
 * Check if a customer has already reviewed a product
 */
export const hasCustomerReviewed = async (
  productId: string,
  customerPhone: string
): Promise<boolean> => {
  if (!customerPhone) return false;

  if (!isFirebaseConfigured() || !db) {
    const reviews = getLocalReviews();
    return reviews.some(
      r => r.productId === productId &&
        r.customerPhone === customerPhone
    );
  }

  try {
    const reviewsRef = collection(db, 'reviews');
    const q = query(
      reviewsRef,
      where('productId', '==', productId),
      where('customerPhone', '==', customerPhone),
      limit(1)
    );

    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('❌ Error checking customer review:', error);
    return false;
  }
};

/**
 * Get orders eligible for review follow-up (completed X days ago, no review yet)
 */
export const getOrdersForFollowUp = async (daysAgo: number = 7): Promise<any[]> => {
  if (!isFirebaseConfigured() || !db) {
    return [];
  }

  try {
    // Calculate date range
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() - daysAgo);
    targetDate.setHours(0, 0, 0, 0);

    const nextDay = new Date(targetDate);
    nextDay.setDate(nextDay.getDate() + 1);

    // Get completed orders
    const ordersRef = collection(db, 'online-orders');
    const q = query(
      ordersRef,
      where('status', '==', 'completed'),
      orderBy('createdAt', 'desc'),
      limit(100)
    );

    const snapshot = await getDocs(q);
    const orders = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Filter to orders from target date
    const filteredOrders = orders.filter(order => {
      const orderDate = (order as any).createdAt?.toDate?.() || new Date((order as any).createdAt);
      return orderDate >= targetDate && orderDate < nextDay;
    });

    // Check which orders haven't been reviewed yet
    const ordersWithoutReviews = [];

    for (const order of filteredOrders) {
      const reviewsRef = collection(db, 'reviews');
      const reviewQuery = query(
        reviewsRef,
        where('orderId', '==', (order as any).orderNumber),
        limit(1)
      );
      const reviewSnapshot = await getDocs(reviewQuery);

      if (reviewSnapshot.empty) {
        ordersWithoutReviews.push(order);
      }
    }

    return ordersWithoutReviews;
  } catch (error) {
    console.error('❌ Error fetching orders for follow-up:', error);
    return [];
  }
};

// ============================================
// EXPORTS
// ============================================

export default {
  // Helpers
  generateReviewId,
  generateReviewToken,
  generateDiscountCode,
  formatReviewDate,

  // Token system (NEW)
  createReviewLink,
  validateReviewToken,
  markTokenAsUsed,
  validateLegacyReviewAccess,

  // Discount codes (NEW)
  createReviewDiscountCode,
  validateReviewDiscountCode,
  markDiscountCodeAsUsed,

  // WhatsApp messages
  generateReviewFollowUpMessage,
  generateBulkFollowUpMessages,

  // Review operations
  submitReview,
  getProductReviews,
  getAllApprovedReviews,
  getProductRating,
  getAllProductRatings,
  getRecentReviews,
  hasCustomerReviewed,
  getOrdersForFollowUp,
};
